import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import { styled, useTheme } from '@mui/material/styles';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import clsx from 'clsx';
import { memo, useMemo, useState } from 'react';
import {
	useFloating,
	FloatingPortal,
	offset,
	flip,
	shift,
	autoUpdate,
	useHover,
	useInteractions,
	useDismiss,
	type Placement,
} from '@floating-ui/react';
import { ListItemButton } from '@mui/material';
import type { ListItemButtonProps } from '@mui/material';
import isUrlInChildren from '@fuse/core/FuseNavigation/isUrlInChildren';
import type { WithRouterProps } from '@fuse/core/withRouter/withRouter';
import usePathname from '@fuse/hooks/usePathname';
import FuseNavItem from '../../FuseNavItem';
import type { FuseNavItemComponentProps } from '../../FuseNavItem';
import FuseSvgIcon from '../../../FuseSvgIcon';

const Root = styled(ListItemButton)<ListItemButtonProps>(({ theme }) => ({
	color: theme.palette.text.primary,
	cursor: 'pointer',
	'&.active, &.active:hover, &.active:focus': {
		backgroundColor: `${theme.palette.secondary.main}!important`,
		color: `${theme.palette.secondary.contrastText}!important`,
		'& .fuse-list-item-text-primary': {
			color: 'inherit',
		},
		'& .fuse-list-item-icon': {
			color: 'inherit',
		},
	},
	'& .fuse-list-item-text': {
		padding: '0 0 0 16px',
	},
	'&.level-0': {
		minHeight: 36,
		borderRadius: 8,
		'&:hover': {
			background: 'transparent',
		},
	},
}));

type FuseNavHorizontalGroupProps = FuseNavItemComponentProps & WithRouterProps;

/**
 * FuseNavHorizontalGroup.
 * Represents a horizontal group component used in the Fuse navigation navigation list.
 * It shows the list item as well as its children with a flyout effect.
 */
function FuseNavHorizontalGroup(props: FuseNavHorizontalGroupProps) {
	const { item, nestedLevel, dense, checkPermission } = props;
	const [opened, setOpened] = useState(false);
	const pathname = usePathname();
	const theme = useTheme();
	const component = item.url ? NavLinkAdapter : 'li';

	const itemProps = useMemo(
		() => ({
			...(component !== 'li' && {
				disabled: item.disabled,
				to: item.url,
				end: item.end,
				role: 'button',
				exact: item?.exact,
			}),
		}),
		[item, component],
	);

	const popperPlacement: Placement = useMemo(() => {
		if (nestedLevel === 0) {
			return theme.direction === 'ltr' ? 'bottom-start' : 'bottom-end';
		}
		return theme.direction === 'ltr' ? 'right' : 'left';
	}, [nestedLevel, theme.direction]);

	const { refs, floatingStyles, context } = useFloating({
		open: opened,
		onOpenChange: setOpened,
		placement: popperPlacement,
		middleware: [offset(0), flip(), shift()],
		whileElementsMounted: autoUpdate,
	});

	const hover = useHover(context, {
		delay: { open: 0, close: 150 },
	});

	const dismiss = useDismiss(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss]);

	const memoizedContent = useMemo(() => {
		return (
			<>
				<div
					ref={refs.setReference}
					{...getReferenceProps()}
				>
					<Root
						component={component}
						className={clsx(
							'fuse-list-item',
							'relative',
							`level-${nestedLevel || 0}`,
							isUrlInChildren(item, pathname) && 'active',
						)}
						aria-owns={opened ? 'menu-fuse-list-grow' : undefined}
						aria-haspopup="true"
						sx={item.sx}
						{...itemProps}
					>
						{item.icon && typeof item.icon === 'string' && (
							<FuseSvgIcon
								color="action"
								className={clsx('fuse-list-item-icon shrink-0', item.iconClass)}
							>
								{item.icon}
							</FuseSvgIcon>
						)}

						<ListItemText
							className="fuse-list-item-text"
							primary={item.title}
							classes={{ primary: 'text-md truncate' }}
						/>

						{(nestedLevel || 0) > 0 && (
							<IconButton
								disableRipple
								className="h-4 w-4 p-0 ltr:ml-1 rtl:mr-1"
								color="inherit"
							>
								<FuseSvgIcon
									size={16}
									className="arrow-icon"
								>
									{theme.direction === 'ltr' ? 'heroicons-outline:chevron-right' : 'heroicons-outline:chevron-left'}
								</FuseSvgIcon>
							</IconButton>
						)}
					</Root>
				</div>
				<FloatingPortal>
					{opened && (
						<div
							ref={refs.setFloating}
							style={{
								...floatingStyles,
								zIndex: 999 + (nestedLevel || 0),
							}}
							className={clsx('z-999', !opened && 'pointer-events-none')}
							{...getFloatingProps()}
						>
							<Grow
								in={opened}
								id="menu-fuse-list-grow"
								style={{ transformOrigin: '0 0 0' }}
							>
								<Paper className="rounded-sm">
									{item.children && (
										<ul className={clsx('popper-navigation-list', dense && 'dense', 'px-0')}>
											{item.children.map((_item) => (
												<FuseNavItem
													key={_item.id}
													type={`horizontal-${_item.type}`}
													item={_item}
													nestedLevel={nestedLevel}
													dense={dense}
												/>
											))}
										</ul>
									)}
								</Paper>
							</Grow>
						</div>
					)}
				</FloatingPortal>
			</>
		);
	}, [
		refs,
		getReferenceProps,
		component,
		nestedLevel,
		item,
		pathname,
		opened,
		itemProps,
		floatingStyles,
		getFloatingProps,
		dense,
	]);

	if (checkPermission && !item?.hasPermission) {
		return null;
	}

	return memoizedContent;
}

const NavHorizontalGroupWithMemo = memo(FuseNavHorizontalGroup);

export default NavHorizontalGroupWithMemo;
